import React, { FC } from 'react';

import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { CategoriesTree } from '@domains/categories';

import { makeCategoryRouteFromSource } from '@utils/category/category';
import { isValueSet } from '@utils/guards';

import { Picture } from '@components/common/Picture/Picture';
import { ChevronDownIcon } from '@components/icons';
import { PlaceholderIcon } from '@components/icons/PlaceholderIcon';

import styles from './CategoryDropdown.module.scss';

type CategoryDropdownProps = {
    categoriesTree: CategoriesTree;
    className?: string;
};

export const CategoryDropdown: FC<CategoryDropdownProps> = (props) => {
    const { categoriesTree, className } = props;
    const router = useRouter();

    const onValueChange = (slug: string) => {
        setTimeout(() => {
            router.push(makeCategoryRouteFromSource(slug, categoriesTree.source), undefined, {
                scroll: false,
            });
        }, 50);
    };

    if (!categoriesTree.length) {
        return null;
    }

    return (
        <Select.Root onValueChange={onValueChange}>
            <Select.Trigger className={clsx(styles.trigger, className)}>
                <div className={styles.triggerContent}>
                    <Select.Value placeholder="Все товары" className={styles.value} />
                </div>
                <Select.Icon className={styles.dropdownIconWrapper}>
                    <ChevronDownIcon className={styles.dropdownIcon} />
                </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Content
                    className={styles.content}
                    position="popper"
                    side="bottom"
                    align="start"
                    avoidCollisions
                    ref={(instance) => {
                        if (!instance) return;
                        // FIX PROPAGATION
                        instance.ontouchend = (e) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            e.cancelable && e.preventDefault();
                        };
                    }}
                >
                    <Select.Viewport className={styles.viewport}>
                        {categoriesTree.map((category) => (
                            <Select.Item
                                key={category.id}
                                value={category.slug}
                                className={styles.item}
                            >
                                <div className={styles.categoryLink}>
                                    {isValueSet(category.thumbnail) ? (
                                        <Picture
                                            className={styles.categoryImage}
                                            classes={{ picture: styles.categoryPicture }}
                                            sources={category.thumbnail}
                                        />
                                    ) : (
                                        <div
                                            className={clsx(
                                                styles.placeholder,
                                                styles.categoryPicture,
                                            )}
                                        >
                                            <PlaceholderIcon />
                                        </div>
                                    )}
                                    <Select.ItemText>{category.name}</Select.ItemText>
                                </div>
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    );
};
